import { HistoricalPercentageChangeDataItem } from 'widgets/historicalPercentageChange/data/historicalPercentageChangeDataItem.ts';
import orderBy from 'lodash/orderBy';
import isNil from 'lodash/isNil';
import mean from 'lodash/mean';
import { today } from 'utils/dates.ts';
import sortBy from 'lodash/sortBy';

export type ChartDataItem = HistoricalPercentageChangeDataItem & {
    normalisedPrice?: number;
    xIndex: number;
};

export function computeChartData(
    alldata: HistoricalPercentageChangeDataItem[],
    periodQty: number
): {
    averageVolume: number | undefined;
    chartData: ChartDataItem[];
    volumeToPrice: (volume: number) => number;
} {
    if (alldata.length === 0) {
        return {
            averageVolume: undefined,
            chartData: [],
            volumeToPrice: () => 0,
        };
    }

    const data = alldata.slice(alldata.length - periodQty);
    const prices = data.map((i) => i.price).filter((p) => p !== undefined);
    const sortedPrices = sortBy(prices);

    const minPrice = sortedPrices[0]! * 0.99;
    const maxPrice = sortedPrices[sortedPrices.length - 1]! * 1.01;
    const priceRange = maxPrice - minPrice;

    const volumes = data.filter((d) => !isNil(d.volume));
    const volumesForAvgVolCalc = volumes.filter(excludeTodaysVolume);
    const averageVolume =
        volumesForAvgVolCalc.length > 0
            ? mean(volumesForAvgVolCalc.map((d) => d.volume))
            : undefined;

    const maxVolume = Math.max(...data.map((i) => i.volume ?? 0));

    const multiplier = maxVolume / priceRange;

    const toNormalisedPrice = (p: number | undefined) =>
        p === undefined ? undefined : (p - minPrice) * multiplier;
    const volumeToPrice = (v: number) => {
        const position = v / maxVolume;
        return priceRange * position + minPrice;
    };

    const toChartItem = (
        item: HistoricalPercentageChangeDataItem,
        index: number
    ) => ({
        ...item,
        normalisedPrice: toNormalisedPrice(item.price),
        xIndex: index,
    });

    const chartData = orderBy(data, ['date'], ['asc']).map((d, i) =>
        toChartItem(d, i)
    );

    return { averageVolume, chartData, volumeToPrice };
}

function excludeTodaysVolume(
    item: HistoricalPercentageChangeDataItem
): boolean {
    return item.date < today();
}
